import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { ApiStatus } from "../utils/api.types";
import { IUserSliceState } from "../utils/user.types";
import { addUser, deleteUser, fetchUserById, fetchUsers, updateUser, verifyInviteToken, updateUserInvite } from "./userThunk";

/**
 * User slice initial state.
 */
const initialState: IUserSliceState = {
    users: [],
    total: 0,
    status: ApiStatus.IDLE,
    errorMessage: '',
    userById: undefined
}

/**
 * User Slice.
 */
const userSlice = createSlice({
    name: 'user',
    initialState: initialState,
    reducers: {

    },
    extraReducers: (builder) => {
        builder
            // Fetch Users
            .addCase(fetchUsers.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.users = action.payload.users || [];
                // Fall back to the page length when the server did not send a
                // total (unpaged callers), so the grid footer is never 0.
                state.total = action.payload.total ?? (action.payload.users || []).length;
                state.errorMessage = '';
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })

            // Fetch User by userId
            .addCase(fetchUserById.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchUserById.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.userById = action.payload || [];
                state.errorMessage = '';
            })
            .addCase(fetchUserById.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })

            // Add User
            .addCase(addUser.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(addUser.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.users.push(action.payload.user);
                state.errorMessage = '';

                toast.success(action.payload.message);
            })
            .addCase(addUser.rejected, (state) => {
                state.status = ApiStatus.FAILED;
            })
            // Delete User
            .addCase(deleteUser.pending, (state) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';

                const { id, message } = action.payload;
                state.users = state.users.filter(user => user._id !== id)

                toast.success(message);
            })
            .addCase(deleteUser.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
            })
            // Update User
            .addCase(updateUser.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                const { message, user: updatedUser } = action.payload
                state.users = state.users
                    .map(user => (user._id === updatedUser._id ? { ...user, ...updatedUser } : user));

                state.errorMessage = '';

                toast.success(message);
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
            })
            .addCase(verifyInviteToken.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';
                const { message } = action.payload;
                toast.success(message);
            })
            .addCase(updateUserInvite.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateUserInvite.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';
                const { message } = action.payload;
                toast.success(message);
            })
            .addCase(updateUserInvite.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
            })
    }
});

export default userSlice.reducer;
